import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sends a General HR Note to each recipient's HR inbox
export const StaffMessageModal = ({ recipients, onClose, onSent, getAuthHeaders }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [sending, setSending] = useState(false);
  useEffect(() => { setTitle(''); setBody(''); setPriority('normal'); }, [recipients]);

  const send = async () => {
    if (!title.trim()) return toast.error('Add a subject');
    setSending(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/hr-notices/notice/bulk`, {
        staff_usernames: recipients, type: 'note', title: title.trim(), description: body.trim(), priority,
      }, { headers: getAuthHeaders() });
      toast.success(`Delivered to ${r.data.sent?.length ?? recipients.length} HR inbox${recipients.length > 1 ? 'es' : ''}`);
      onSent?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const field = 'w-full px-3 py-2.5 bg-black/40 border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40';
  return (
    <Dialog open={!!recipients} onOpenChange={onClose}>
      <DialogContent className="bg-[#0b0b16] border-white/[0.08] text-white max-w-lg" data-testid="staff-message-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-cyan-200"><Send className="w-4 h-4" /> Message {recipients?.length} staff</DialogTitle></DialogHeader>
        <p className="text-[11px] text-white/45 -mt-1">Delivered as a General HR Note into each person's HR Inbox.</p>
        <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
          {(recipients || []).map(u => <span key={u} className="px-2 py-0.5 rounded-full text-[10px] bg-white/[0.05] border border-white/[0.08] text-white/60">@{u}</span>)}
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Subject" className={field} data-testid="staff-message-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Write your message…" className={`${field} resize-none`} data-testid="staff-message-body" />
        <div className="flex items-center gap-2">
          {['normal', 'high'].map(p => (
            <button key={p} onClick={() => setPriority(p)} data-testid={`staff-message-priority-${p}`}
              className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] border transition-colors ${priority === p ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-100' : 'border-white/[0.08] text-white/45'}`}>{p}</button>
          ))}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs text-white/50 hover:text-white">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="staff-message-send"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-cyan-400 to-violet-400 disabled:opacity-50">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
